class CartIncentiveProgress extends HTMLElement {
  async connectedCallback() {
    this.#cacheElements();
    this.#qualifyingIds = this.#parseIdSet(this.dataset.qualifyingIds);
    this.#matchAll = this.dataset.matchAll === 'true';
    this.#goal = Math.max(1, this.#parseInteger(this.dataset.goal, 1));

    await this.#refreshFromCart();
    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:updated', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:updated', this.#handleCartUpdate);
  }

  #elements;
  #qualifyingIds = new Set();
  #matchAll = true;
  #goal = 1;

  #handleCartUpdate = async () => {
    await this.#refreshFromCart();
  };

  #cacheElements() {
    this.#elements = {
      message: this.querySelector('[data-message]'),
      progressBar: this.querySelector('[data-progress-bar]'),
      progressFill: this.querySelector('[data-progress-fill]'),
    };
  }

  async #refreshFromCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      this.#renderFromCart(cart);
    } catch {
      // Keep the last rendered state on network errors.
    }
  }

  #renderFromCart(cart) {
    const qualifyingCount = this.#getQualifyingCount(cart);
    const goal = this.#goal;
    const isQualified = qualifyingCount >= goal;
    const remaining = Math.max(0, goal - qualifyingCount);
    const progressPercent = isQualified
      ? 100
      : Math.max(0, Math.min(100, Math.round((qualifyingCount * 100) / goal)));

    this.style.setProperty('--cart-incentive-progress-value', `${progressPercent}%`);

    if (this.#elements.progressBar) {
      this.#elements.progressBar.setAttribute('aria-valuenow', String(progressPercent));
      this.#elements.progressBar.setAttribute(
        'aria-label',
        this.dataset.progressLabel || 'Cart incentive progress'
      );
    }

    if (!this.#elements.message) return;

    if (isQualified) {
      this.#elements.message.textContent = this.dataset.qualifiedMessage || 'Promo unlocked!';
      return;
    }

    let template;
    if (qualifyingCount <= 0) {
      template =
        this.dataset.initialTemplate ||
        this.dataset.progressTemplate ||
        'Add {count} more to unlock the promo!';
    } else if (remaining === 1) {
      template =
        this.dataset.progressTemplateSingular ||
        this.dataset.progressTemplate ||
        'Add {count} more to unlock the promo!';
    } else {
      template = this.dataset.progressTemplate || 'Add {count} more to unlock the promo!';
    }

    this.#elements.message.textContent = template.replace('{count}', String(remaining));
  }

  #getQualifyingCount(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let total = 0;
    for (const item of items) {
      const quantity = this.#parseInteger(item?.quantity, 0);
      if (quantity <= 0) continue;
      if (this.#matchAll) {
        total += quantity;
        continue;
      }
      const productId = this.#parseInteger(item?.product_id, 0);
      if (productId && this.#qualifyingIds.has(productId)) {
        total += quantity;
      }
    }
    return total;
  }

  #parseIdSet(value) {
    if (!value) return new Set();
    return new Set(
      value
        .split(',')
        .map((entry) => Number.parseInt(entry, 10))
        .filter((entry) => Number.isFinite(entry) && entry > 0)
    );
  }

  #parseInteger(value, fallback) {
    const parsed = Number.parseInt(value ?? '', 10);
    if (Number.isNaN(parsed)) return fallback;
    return parsed;
  }
}

if (!customElements.get('cart-incentive-progress')) {
  customElements.define('cart-incentive-progress', CartIncentiveProgress);
}
